import Uppy, { UppyFile } from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { useCallback, useEffect, useState } from "react";
import { FileProgress, UploadResult, FileProgressStage, UploadError } from "../types";
import { apiFetch } from "../utilities/api";
import { uploadBatchService } from "./uploadBatchService";

/**
 * Sanitize filename for S3 storage to prevent URL encoding issues.
 * 
 * Replaces spaces and special characters with underscores to avoid problems with:
 * - MediaConvert manifest URL references (spaces + plus signs cause 403 errors)
 * - CloudFront URL encoding (form-encoded vs standard encoding conflicts)
 * 
 * @param filename - Original filename from user's filesystem
 * @returns Sanitized filename safe for S3 keys and streaming URLs
 */
function sanitizeFilename(filename: string): string {
  // Replace all non-alphanumeric characters (except dots and hyphens) with underscores
  // This handles: spaces, +, (, ), [, ], #, &, etc.
  let sanitized = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  // Remove leading underscores and dots (e.g., "__test.jpg" → "test.jpg", ".mp4" → "mp4")
  sanitized = sanitized.replace(/^[_.]+/, '');
  
  // Fallback if filename is empty after sanitization
  if (!sanitized) {
    sanitized = 'upload';
  }
  
  // Normalize file extension to lowercase
  const lastDotIndex = sanitized.lastIndexOf('.');
  if (lastDotIndex > 0) {
    const name = sanitized.substring(0, lastDotIndex);
    const ext = sanitized.substring(lastDotIndex).toLowerCase();
    sanitized = name + ext;
  }
  
  return sanitized;
}


/**
 * AWS S3 Upload Service
 *
 * This service handles file uploads using Uppy with automatic method selection:
 * - Small files (≤threshold): Single-part uploads with signed URLs (fast, simple)
 * - Large files (>threshold): Multipart uploads with batch-signed URLs (parallel, resumable)
 *
 * ⚠️  CHUNK SIZE CONFIGURATION ⚠️
 * The chunk size and multipart threshold are now provided by the backend in the
 * multipart initialization response. This ensures frontend and backend are always aligned.
 * 
 * No more hardcoded constants to keep in sync!
 *
 * Flow:
 * 1. Create media record in Django database with metadata
 * 2. Small files: Get signed URL for direct S3 upload
 *    Large files: Initialize multipart upload - backend returns calculation details
 * 3. Configure Uppy with backend's chunk size settings
 * 4. Upload file(s) directly to S3 using signed URL(s)
 * 5. Update Django record with completion status
 *
 * Benefits:
 * - Automatic method selection based on file size
 * - Parallelism and resumability for large files
 * - Simple and reliable for small files
 * - Efficient batch URL generation (avoiding N API calls per chunk)
 *
 * 1. Create media record in Django database with metadata (name, description, relationships)
 *    - This gives us a database record before the file exists in S3
 *    - Django returns a signed URL for S3 upload
 *
 * 2. Use signed URL to upload file directly to S3
 *    - Client -> S3 direct upload (more efficient than going through our server)
 *
 * 3. Update Django record with S3 upload status and details
 *    - Marks upload as complete
 *    - Stores S3 location details
 *
 * While this might seem like a violation of separation of concerns, the Django interaction
 * is necessary here because:
 * - We need the media record to exist before S3 upload
 * - The signed URL must come from our backend for security
 * - This ensures metadata consistency between database and S3
 *
 * @module awsUploadService
 */

// Define metadata interface that extends Uppy's internal metadata
interface UppyMeta extends Record<string, any> {
  jobId: string;
  fileId: string;
  relationships?: Record<string, any>;
  name?: string;
  description?: string;
}

// Update the UppyError interface
interface UppyError {
  message?: string;
  request?: {
    status?: number;
    responseText?: string;
    responseURL?: string;
  };
  response?: {
    status?: number;
    statusText?: string;
  };
}

// Default fallback values (only used if backend doesn't provide them)
const FALLBACK_MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5MB
const FALLBACK_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Store backend configuration globally to avoid re-fetching
 */
let backendConfig: {
  multipartThreshold: number;
  chunkSize: number;
} | null = null;

/**
 * AWS S3 Upload Service
 * 
 * Current Implementation:
 * - Uses single-part uploads with signed URLs for files up to 200MB
 * 
 * Future Considerations for Large Files (>200MB):
 * To implement multipart uploads, we would need to:
 * 1. Add backend endpoints for multipart operations (init, sign, complete)
 * 2. Configure S3 CORS for multipart
 * 3. See: https://uppy.io/docs/aws-s3-multipart/
 */

// Create a function to initialize Uppy with AWS S3 configuration
export const createUppy = () => {
  return new Uppy({
    debug: process.env.NODE_ENV === "development",
    autoProceed: false,
    allowMultipleUploadBatches: true,
    restrictions: {
      maxFileSize: 4000 * 1024 * 1024, // 200MB
      allowedFileTypes: [
        "image/*",
        "video/*",
        "audio/*",
        "application/pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".MTS",
        ".mts",
        ".m4v",
        ".M4V",
        ".vob",
        ".VOB",
        ".avi",
        ".AVI",
        ".mkv",
        ".MKV",
      ],
    },
  }).use(AwsS3, {
    getUploadParameters: async (file) => {
      // Sanitize filename for S3 storage to prevent URL encoding issues
      const sanitizedFilename = sanitizeFilename(file.name || 'upload');
      
      // Create media entry first to get signed URL
      const mediaResponse = await apiFetch(
        `${process.env.REACT_APP_API_ROOT}api/user/media/?return_signed_url=true`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: file.meta.displayName || file.meta.name || file.name,
            description: file.meta.description,
            media_type: file.type,
            file_type: file.type,
            file_size: file.size,
            original_file: {
              filename: sanitizedFilename,
              original_filename: file.name,
              type: file.type,
              size: `${Math.round((file.size ?? 0) / 1024)}kb`
            },
            relationships: file.meta.relationships,
            upload_details: {
              status: "pending",
              batch_id: file.meta.batch_id,
              upload_method: "signed_url",
            },
          }),
        }
      );

      // Store media ID in file meta for later use
      file.meta.mediaId = mediaResponse.id;

      // Extract signed URL from the response
      const signedUrl = mediaResponse.upload_details?.signed_url;
      if (!signedUrl) {
        throw new Error("No signed URL returned from server");
      }

      return {
        method: "PUT",
        url: signedUrl,
        fields: {},
        headers: {
          "Content-Type": file.type,
        },
      };
    },

    shouldUseMultipart: (file) => {
      const size = file.size ?? 0;
      const threshold = backendConfig?.multipartThreshold ?? FALLBACK_MULTIPART_THRESHOLD;
      return size > threshold;
    },

    // ⚠️  DYNAMIC CONFIGURATION: Chunk size determined by backend response
    getChunkSize: (file) => {
      return backendConfig?.chunkSize ?? FALLBACK_CHUNK_SIZE;
    },

    createMultipartUpload: async (file) => {
      try {
        // Sanitize filename for S3 storage
        const sanitizedFilename = sanitizeFilename(file.name || 'upload');
        
        // Create media entry first
        const mediaResponse = await apiFetch(
          `${process.env.REACT_APP_API_ROOT}api/user/media/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: file.meta.displayName || file.meta.name || file.name,
              description: file.meta.description,
              media_type: file.type,
              file_type: file.type,
              file_size: file.size,
              original_file: {
                filename: sanitizedFilename,
                original_filename: file.name,
                type: file.type,
                size: `${Math.round((file.size ?? 0) / 1024)}kb`
              },
              relationships: file.meta.relationships,
              upload_details: {
                status: "pending",
                batch_id: file.meta.batch_id,
                upload_method: "s3_multipart",
              },
            }),
          }
        );

        // Store media ID in file meta for later use
        file.meta.mediaId = mediaResponse.id;

        // ⚠️  CRITICAL: Let backend calculate parts and provide configuration
        const fileSize = file.size ?? 0;
        
        // First call - frontend sends its guess, backend validates and responds with config
        const result = await apiFetch(
          `${process.env.REACT_APP_API_ROOT}api/user/media/multipart/initialize/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              filename: sanitizedFilename,  // Use sanitized filename for S3 key
              fileType: file.type,
              fileSize: fileSize,
              parts: Math.ceil(fileSize / (backendConfig?.chunkSize ?? FALLBACK_CHUNK_SIZE)), // Frontend's best guess
              media_id: mediaResponse.id,
            }),
          }
        );
        
        // ⚠️  CRITICAL: Backend returns calculation details - use as single source of truth
        const calculation = result.calculation;
        if (!calculation) {
          throw new Error("Backend did not return calculation details");
        }
        
        // Store backend configuration for future uploads in this session
        backendConfig = {
          multipartThreshold: calculation.multipart_threshold,
          chunkSize: calculation.chunk_size
        };
        
        // Validate we have enough URLs
        if (result.urls.length < calculation.parts_needed) {
          throw new Error(
            `Insufficient pre-signed URLs! Need ${calculation.parts_needed} parts but received ${result.urls.length} URLs. ` +
            `File size: ${calculation.file_size} bytes, chunk size: ${calculation.chunk_size} bytes`
          );
        }
        
        // Store the URLs for later use in signPart
        file.meta.preSignedUrls = result.urls;
        file.meta.calculation = calculation; // Store calculation for debugging
        
        return {
          uploadId: result.upload_id,
          key: `org_${mediaResponse.organisation}/${mediaResponse.id}/${sanitizedFilename}`,
        };
      } catch (error) {
        console.error("Failed to create multipart upload:", error);
        throw error;
      }
    },

    listParts: async (file, { uploadId, key, signal }) => {
      try {
        // Since we get all URLs upfront, we don't need to list parts individually
        return [];
      } catch (error) {
        console.error("Failed to list parts:", error);
        throw error;
      }
    },

    signPart: async (file, { partNumber, uploadId, key, signal }) => {
      try {
        // Use pre-signed URLs from initialization instead of individual API calls
        const preSignedUrls = file.meta.preSignedUrls;
        if (!preSignedUrls || !Array.isArray(preSignedUrls)) {
          throw new Error("Pre-signed URLs not available");
        }
        
        const url = preSignedUrls[partNumber - 1]; // Array is 0-indexed, partNumber is 1-indexed
        if (!url) {
          console.error(`[UPLOAD_SERVICE] No pre-signed URL for part ${partNumber}. Available URLs: ${preSignedUrls.length}`);
          throw new Error(`No pre-signed URL for part ${partNumber}`);
        }
        
        return {
          url: url,
          headers: {},
        };
      } catch (error) {
        console.error("Failed to get pre-signed URL for part:", error);
        throw error;
      }
    },

    abortMultipartUpload: async (file, { uploadId, key, signal }) => {
      try {
        if (!uploadId || !key) {
          throw new Error("Missing uploadId or key");
        }

        await apiFetch(
          `${process.env.REACT_APP_API_ROOT}api/user/media/multipart/abort/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ upload_id: uploadId }),
            signal,
          }
        );
      } catch (error) {
        console.error("Failed to abort multipart upload:", error);
        throw error;
      }
    },

    completeMultipartUpload: async (file, { uploadId, key, parts, signal }) => {
      try {
        if (!uploadId || !key || !parts) {
          throw new Error("Missing required parameters");
        }

        // Filter parts to only include ETag and PartNumber (AWS requirement)
        const filteredParts = parts.map(part => ({
          ETag: part.ETag, // Use only the uppercase ETag property
          PartNumber: part.PartNumber
        }));

        const result = await apiFetch(
          `${process.env.REACT_APP_API_ROOT}api/user/media/multipart/complete/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              upload_id: uploadId,
              parts: filteredParts, // Use filtered parts instead of raw parts
              filename: file.name,
            }),
            signal,
          }
        );
        return {
          location: result.location,
        };
      } catch (error) {
        console.error("Failed to complete multipart upload:", error);
        throw error;
      }
    },
  });
};

// Hook for managing uploads
export const useS3Upload = () => {
  const [uppy] = useState(createUppy);
  const [progress, setProgress] = useState<Record<string, FileProgress>>({});
  const [errors, setErrors] = useState<UploadError[]>([]);

  useEffect(() => {
    if (!uppy) return;

    const handleProgress = (
      file: UppyFile<Record<string, any>, Record<string, any>>,
      progressData: { bytesUploaded: number; bytesTotal: number | null }
    ) => {
      // Use frontend ID for consistent progress tracking
      const frontendId = file.meta?.frontendId;
      if (!frontendId || !progressData.bytesTotal) {
        return;
      }

      const progressPercent = Math.round((progressData.bytesUploaded / progressData.bytesTotal!) * 100);

      setProgress(prev => {
        const newProgress = {
          ...prev,
          [frontendId]: {
            stage: 'uploading' as FileProgressStage,
            completed: progressData.bytesUploaded,
            total: progressData.bytesTotal!,
            progress: progressPercent
          }
        };
        return newProgress;
      });
    };

    const handleUploadStart = (
      file: UppyFile<Record<string, any>, Record<string, any>>
    ) => {
      // Use frontend ID for consistent progress tracking
      const frontendId = file.meta?.frontendId;
      if (!frontendId) {
        return;
      }

      setProgress(prev => {
        const newProgress = {
          ...prev,
          [frontendId]: {
            stage: 'presigning' as FileProgressStage,
            completed: 0,
            total: file.size || 0,
            progress: 0
          }
        };
        return newProgress;
      });
    };

    const handleUploadError = (
      file: UppyFile<Record<string, any>, Record<string, any>>,
      error: Error
    ) => {
      setErrors(prev => [...prev, {
        file: file.name || 'Unknown file',
        stage: 'upload',
        error: error.message || 'Upload failed'
      }]);
    };

    uppy.on('upload-progress', handleProgress as any);
    uppy.on('file-added', handleUploadStart as any);
    uppy.on('upload-error', handleUploadError as any);

    return () => {
      uppy.off('upload-progress', handleProgress as any);
      uppy.off('file-added', handleUploadStart as any);
      uppy.off('upload-error', handleUploadError as any);
    };
  }, [uppy]);

  const uploadFiles = useCallback(async (
    files: File[],
    metadata: Array<{
      id: string | number;
      name: string;
      description: string;
      relationships?: Record<string, any>;
    }>
  ) => {
    try {
      uppy.cancelAll();
      setProgress({});
      setErrors([]); // Clear previous errors

      const batch = await uploadBatchService.createBatch(files);
      const startTime = Date.now();

      // Add files to Uppy
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileMetadata = metadata[i];
        
        setProgress(prev => ({
          ...prev,
          [fileMetadata.id]: {
            stage: 'pending' as FileProgressStage,
            completed: 0,
            total: file.size,
            progress: 0
          }
        }));

        const uppyFileId = uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          meta: { 
            frontendId: fileMetadata.id, // Store frontend ID for progress tracking
            displayName: fileMetadata.name,
            name: fileMetadata.name,
            description: fileMetadata.description,
            relationships: fileMetadata.relationships,
            batch_id: batch.id 
          }
        });
      }
      const result = await uppy.upload();
      
      if (!result?.successful || !result?.failed) {
        const errorDetails = {
          type: 'upload_failure',
          message: 'Upload failed: No result returned',
          duration: Date.now() - startTime
        };
        
        await uploadBatchService.updateBatchStatus(batch.id, "failed", errorDetails);
        
        throw new Error(errorDetails.message);
      }

      if (result.failed.length > 0) {
        const failedUploads = result.failed.map(f => {
          let errorMessage = 'Unknown error';
          let errorType = 'UNKNOWN_ERROR';

          // Log the raw error for debugging
          console.debug('Raw Uppy S3 error:', f.error);

          if (typeof f.error === 'string') {
            errorMessage = f.error;
            // Common S3 error patterns in string format
            if (f.error.includes('403')) {
              errorType = 'PERMISSION_ERROR';
              errorMessage = 'Access denied - CORS settings need to be updated';
            } else if (f.error.includes('Network Error')) {
              errorType = 'NETWORK_ERROR';
              errorMessage = 'Network connection error';
            }
          } else if (typeof f.error === 'object' && f.error !== null) {
            const error = f.error as UppyError;
            
            // Handle S3 specific errors
            if (error.response?.status === 403) {
              errorType = 'PERMISSION_ERROR';
              errorMessage = 'Access denied - Check CORS configuration';
            } else if (error.request?.status) {
              errorType = 'REQUEST_ERROR';
              errorMessage = `Request failed (${error.request.status})`;
              
              try {
                if (error.request.responseText) {
                  const responseData = JSON.parse(error.request.responseText);
                  if (responseData.message) {
                    errorMessage = `S3 Error: ${responseData.message}`;
                  }
                }
              } catch (e) {
                // If we can't parse the response, use the raw text
                if (error.request.responseText) {
                  errorMessage = `S3 Error: ${error.request.responseText}`;
                }
              }
            } else if (error.message) {
              errorMessage = error.message;
              
              // S3 specific error patterns
              if (error.message.includes('ETag')) {
                errorType = 'ETAG_ERROR';
                errorMessage = 'File verification failed';
              } else if (error.message.includes('timeout')) {
                errorType = 'TIMEOUT_ERROR';
                errorMessage = 'Upload timed out - try a smaller file or check your connection';
              } else if (error.message.includes('CORS')) {
                errorType = 'CORS_ERROR';
                errorMessage = 'CORS configuration error - contact support';
              }
            }
          }

          return {
            file: f.name || 'Unknown file',
            error: errorMessage,
            error_type: errorType,
            size: f.size || 0,
            type: f.type || 'unknown',
            details: {
              response_status: (f.error as UppyError)?.response?.status,
              response_url: (f.error as UppyError)?.request?.responseURL,
              raw_error: f.error
            }
          };
        });

        const errorDetails = {
          type: 'upload_failure',
          message: failedUploads.map(f => `${f.file}: ${f.error}`).join(', '),
          duration: Date.now() - startTime,
          failed_files: failedUploads,
          error_summary: `Upload failed after ${Math.round((Date.now() - startTime)/1000)}s with ${failedUploads.length} errors`
        };

        await uploadBatchService.updateBatchStatus(batch.id, "failed", errorDetails);

        throw new Error(`Upload failed: ${failedUploads.map(f => `${f.file} (${f.error_type}: ${f.error})`).join(', ')}`);
      }

      // Update media entries with completed status and get final results
      const mediaResults = await Promise.all(
        result.successful.map(async (file) => {
          // Determine upload method based on whether multipart was used
          const wasMultipart = file.meta?.preSignedUrls || file.meta?.uploadId;
          const uploadMethod = wasMultipart ? "s3_multipart" : "signed_url";
          
          // Update media entry with completed status and get response
          const formData = new FormData();
          
          formData.append('upload_details', JSON.stringify({
            status: "completed",
            batch_id: batch.id,
            upload_method: uploadMethod,
          }));

          formData.append('remote_details', JSON.stringify({
            service: "s3",
            url: file.uploadURL || "",
            thumbnail: "",
            id: String(file.meta.mediaId),
            mime: file.type,
            bucket: process.env.REACT_APP_S3_BUCKET!,
            key: file.meta.key || "",
          }));

          const mediaResponse = await apiFetch(
            `${process.env.REACT_APP_API_ROOT}api/user/media/${file.meta.mediaId}/`,
            {
              method: "PATCH",
              body: formData, // Remove Content-Type header to let browser set it
            }
          );

          return {
            file: file.data as File,
            uploadType: "s3" as const,
            remoteDetails: mediaResponse.remote_details,
          };
        })
      );

      // Update batch status to completed
      await uploadBatchService.updateBatchStatus(batch.id, "completed");

      return mediaResults;
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  }, [uppy]);

  return { uppy, uploadFiles, progress, errors };
};
